import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Award,
  ImageOff,
} from "lucide-react";
import FeedAdminGuard from "@/components/feed/FeedAdminGuard";
import ImageUploadField from "@/components/feed/ImageUploadField";
import {
  useKudosTypes,
  useCreateKudosType,
  useUpdateKudosType,
  useDeleteKudosType,
} from "@/hooks/useKudosTypes";
import type {
  KudosTypeResponse,
  KudosTypeRequest,
} from "@/types/generated/cms.generated";

const KUDOS_UPLOAD_ENDPOINT = "/kudos-types/upload-url";

const kudosFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  badgeBlobUrl: z.string().min(1, "A badge image is required"),
  color: z
    .string()
    .regex(/^#([0-9a-fA-F]{6})$/, "Enter a valid 6-digit hex color (e.g. #FF5733)")
    .or(z.literal(""))
    .optional(),
  sortOrder: z.number().default(0),
  isActive: z.boolean().default(true),
});

type KudosFormData = z.infer<typeof kudosFormSchema>;

interface KudosModalProps {
  open: boolean;
  onClose: () => void;
  kudosType?: KudosTypeResponse;
  nextSortOrder: number;
}

function KudosModal({
  open,
  onClose,
  kudosType,
  nextSortOrder,
}: KudosModalProps) {
  const createKudos = useCreateKudosType();
  const updateKudos = useUpdateKudosType();

  const form = useForm<KudosFormData>({
    resolver: zodResolver(kudosFormSchema),
    defaultValues: {
      name: "",
      description: "",
      badgeBlobUrl: "",
      color: "",
      sortOrder: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: kudosType?.name ?? "",
        description: kudosType?.description ?? "",
        badgeBlobUrl: kudosType?.badgeBlobUrl ?? "",
        color: kudosType?.color ?? "",
        sortOrder: kudosType?.sortOrder ?? nextSortOrder,
        isActive: kudosType?.isActive ?? true,
      });
    }
  }, [open, kudosType, nextSortOrder, form]);

  const onSubmit = async (data: KudosFormData) => {
    const payload: KudosTypeRequest = {
      name: data.name,
      description: data.description || null,
      badgeBlobUrl: data.badgeBlobUrl,
      color: data.color || null,
      sortOrder: data.sortOrder,
      isActive: data.isActive,
    };
    try {
      if (kudosType?.id) {
        await updateKudos.mutateAsync({ id: kudosType.id, data: payload });
      } else {
        await createKudos.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Errors handled by hooks.
    }
  };

  const isSaving = createKudos.isPending || updateKudos.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kudosType ? "Edit Kudos Type" : "Create Kudos Type"}
          </DialogTitle>
          <DialogDescription>
            {kudosType
              ? "Update this kudos type's name, badge, color, or order."
              : "Add a new kudos type employees can give in the social feed."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Team Player" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="When should this kudos be given?"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="badgeBlobUrl"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <ImageUploadField
                      label="Badge Image"
                      id="kudos-badge"
                      uploadUrlEndpoint={KUDOS_UPLOAD_ENDPOINT}
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color (Optional)</FormLabel>
                    <FormControl>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="color"
                          className="w-16 h-9 p-1"
                          value={field.value || "#000000"}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                        <Input
                          placeholder="#FF5733"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sortOrder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Inactive kudos types cannot be given by employees.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {kudosType ? "Update" : "Create"} Kudos Type
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function KudosTypesContent() {
  const { data: kudosTypes = [], isLoading, error } = useKudosTypes();
  const updateKudos = useUpdateKudosType();
  const deleteKudos = useDeleteKudosType();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<KudosTypeResponse | undefined>();

  const sorted = useMemo(
    () =>
      [...kudosTypes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [kudosTypes],
  );

  const nextSortOrder = useMemo(
    () => kudosTypes.reduce((max, k) => Math.max(max, k.sortOrder ?? 0), 0) + 1,
    [kudosTypes],
  );

  const toPayload = (k: KudosTypeResponse): KudosTypeRequest => ({
    name: k.name ?? "",
    description: k.description ?? null,
    badgeBlobUrl: k.badgeBlobUrl ?? "",
    color: k.color ?? null,
    sortOrder: k.sortOrder ?? 0,
    isActive: k.isActive ?? true,
  });

  const handleToggleActive = (k: KudosTypeResponse) => {
    if (!k.id) return;
    updateKudos.mutate({
      id: k.id,
      data: { ...toPayload(k), isActive: !k.isActive },
    });
  };

  const handleEdit = (k: KudosTypeResponse) => {
    setEditing(k);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(undefined);
  };

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Kudos Types"
        description="Manage the kudos badges employees can award one another"
      >
        <Button onClick={() => setModalOpen(true)} data-testid="add-kudos-type">
          <Plus className="w-4 h-4 mr-2" />
          Add Kudos Type
        </Button>
      </Header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Kudos Types ({sorted.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error loading kudos types</AlertTitle>
                <AlertDescription>
                  Failed to load the kudos type catalog.
                </AlertDescription>
              </Alert>
            ) : isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : sorted.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No kudos types created yet.
              </div>
            ) : (
              <Table data-testid="kudos-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Badge</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>Sort</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((k) => (
                    <TableRow
                      key={k.id}
                      className={k.isActive ? "" : "opacity-60"}
                      data-testid="kudos-row"
                    >
                      <TableCell>
                        {k.badgeBlobUrl ? (
                          <img
                            src={k.badgeBlobUrl}
                            alt={k.name ?? "Badge"}
                            className="h-10 w-10 object-contain"
                          />
                        ) : (
                          <ImageOff className="h-6 w-6 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="max-w-[240px] truncate">
                        {k.description || "-"}
                      </TableCell>
                      <TableCell>
                        {k.color ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: k.color }}
                            />
                            <span className="text-xs font-mono">{k.color}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{k.sortOrder ?? 0}</TableCell>
                      <TableCell>
                        <Badge variant={k.isActive ? "default" : "secondary"}>
                          {k.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!k.isActive}
                            onCheckedChange={() => handleToggleActive(k)}
                            disabled={updateKudos.isPending}
                            aria-label={`Toggle ${k.name}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(k)}
                            aria-label="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete Kudos Type
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{k.name}"?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    k.id && deleteKudos.mutate(k.id)
                                  }
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <KudosModal
        open={modalOpen}
        onClose={closeModal}
        kudosType={editing}
        nextSortOrder={nextSortOrder}
      />
    </div>
  );
}

export default function KudosTypesPage() {
  return (
    <FeedAdminGuard>
      <KudosTypesContent />
    </FeedAdminGuard>
  );
}
